import { OrganizationCodes } from '@revenuemarket/bank';
import { DenialType, AccountStatus } from '@revenuemarket/trade';

import { API } from '~apis/axios/instance';
import { TaxInvoiceType } from '~models/tax-invoice';
import { Page, ResponseBaseWithData } from '~types/response';

export enum BalanceType {
  CHECKING_ACCOUNT = 'CHECKING_ACCOUNT',
  SAVING_ACCOUNT = 'SAVING_ACCOUNT',
  CREDIT_CARD_LIMIT = 'CREDIT_CARD_LIMIT',
  FX_ACCOUNT = 'FX_ACCOUNT',
  OVERDRAFT_LIMIT = 'OVERDRAFT_LIMIT',
  FUNDS_ACCOUNT = 'FUNDS_ACCOUNT',
  SECURITY_ACCOUNT = 'SECURITY_ACCOUNT',
}

type BalanceScrapeStatus = 'NORMAL' | 'ERROR';

export type BalanceList = BalanceItem[];

export interface BalanceItem {
  balanceType: BalanceType;
  balances: IBalancesItem[];
}
export interface IBalancesItem {
  type: BalanceType;
  key: string;
  balance: number | null;
  currency: string;
  name: string;
  alias?: string;
  localBalance: number | null;
  numOfCards: number;
  organizationCode: OrganizationCodes;
  bankAccountNumber: string;
  diffAmount: number;
  diffAmountKrw: number;
  scrapeStatus: BalanceScrapeStatus;
  accountStatus: AccountStatus;
  isVisible: boolean;
}

export const getTotalBalances = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<{ balances: BalanceList }>>(
    `/api/v3/companies/${companyId}/home-dashboard/total-balances`,
  );
};

export interface SettingResponse {
  companyId: string;
  bankAccountSettings: IBankAccountSettings;
  totalBalanceSettings: ITotalBalanceSettings;
}
interface IBankAccountSettings {
  orders: number[];
  hidden: number[];
}
interface ITotalBalanceSettings {
  groups: IGroupsItem[];
}
export interface IGroupsItem {
  balanceType: BalanceType;
  settings: ISettingsItem[];
  visible: boolean;
}
interface ISettingsItem {
  balanceType: BalanceType;
  key: string;
  visible: boolean;
}

export const getDisplaySettings = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<SettingResponse>>(
    `/api/v3/companies/${companyId}/display-settings`,
  );
};

export const updateDisplaySettings = ({
  companyId,
  groups,
}: {
  companyId: string;
  groups: IGroupsItem[];
}) => {
  return API.put<ResponseBaseWithData<SettingResponse>>(
    `/api/v3/companies/${companyId}/display-settings`,
    {
      data: {
        groups,
      },
    },
  );
};

interface BurnResponse {
  months: number;
  baseDate: string;
  balance: number;
  burn: number;
  runway: number | null;
}

export const getBurn = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<BurnResponse>>(
    `/api/v3/companies/${companyId}/home-dashboard/burn`,
  );
};

export interface ScheduledInvoiceMapping {
  taxInvoiceId: string;
  taxInvoiceType: TaxInvoiceType;
  reportingDate: string;
  issueDate: string;
  counterPartyCompanyName: string;
  repItems: string;
  totalAmount: number;
  mappingAmount: number;
  unmappedAmount: number;
  status: string;
  expectedPaymentDate: string;
  actualPaymentDate: string;
  mappedCount: number;
  counterPartyBusinessRegNumber: string;
  businessEntityId: number;
  counterPartyName: string;
}

export const getScheduledInvoiceMappings = ({
  companyId,
  type,
}: {
  companyId: string;
  type: TaxInvoiceType;
}) => {
  return API.get<ResponseBaseWithData<Page<ScheduledInvoiceMapping>>>(
    `/api/v3/companies/${companyId}/invoice-mappings/scheduled?type=${type}`,
  );
};

export interface BurnSettingResponse {
  months: number;
  baseDate: string;
  categories: ICategoriesItem[];
  excludeHiddenAccounts: boolean;
}
export interface ICategoriesItem {
  categoryId: number;
  category0: string;
  category1: string;
  category2: string;
  category3: string;
  cashFlowType: string;
  sequenceNumber: number;
  included: boolean;
}

export const getBurnSettings = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<BurnSettingResponse>>(
    `/api/v3/companies/${companyId}/home-dashboard/burn/settings`,
  );
};

interface BurnRequest {
  months: number | null;
  baseDate: string | null;
  categories: number[];
  excludeHiddenAccounts: boolean;
}

export const updateBurnSettings = ({
  companyId,
  request,
}: {
  companyId: string;
  request: BurnRequest;
}) => {
  return API.put<ResponseBaseWithData>(
    `/api/v3/companies/${companyId}/home-dashboard/burn/settings`,
    {
      data: request,
    },
  );
};

export interface NoTerm {
  type: 'NO_TERM';
  numOfCumulativeCustomers: number;
  cumulativePayout: number;
}

export interface TermRequested {
  type: 'TERM_REQUESTED';
  termRequestedAt: string;
  termOfferDate: string;
  numOfCumulativeCustomers: number;
  cumulativePayout: number;
}

export interface TermOffered {
  type: 'TERM_OFFERED';
  tradeApplyDate: string;
  finalOffer: string;
  payoutDate: string;
  denialReason?: DenialType;
  payoutLimit: number;
  tradingDiscountRate: number;
  tradingPeriod: number;
  offeredAt: string;
  repaymentSchedules: {
    repaymentRound: number;
    repaymentDate: string;
    repaymentAmount: number;
    principalAmount: number;
    discountAmount: number;
  }[];
  cumulativePayout: number;
  numOfCumulativeCustomers: number;
}

export interface TradeApplied {
  type: 'TRADE_APPLIED';
  payoutDate: string;
  payout: number;
  tradingPeriod: number;
  tradingDiscountRate: number;
  appliedAt: string;
  tradeId: number;
}

export interface TradeBeingRepaid {
  type: 'TRADE_BEING_REPAID';
  tradingPeriod: number;
  payoutDate: string;
  repaymentRound: number;
  repaymentDate: string;
  repaymentAmount: number;
  tradeId: number;
}

export type FinanceCard = NoTerm | TermRequested | TermOffered | TradeApplied | TradeBeingRepaid;

export const getFinanceInfo = ({ companyId }: { companyId: string }) => {
  return API.get<
    ResponseBaseWithData<{
      items: FinanceCard[];
    }>
  >(`/api/v3/companies/${companyId}/home-dashboard/finance`);
};

// Home Insights API
export type TransactionDirection = 'IN' | 'OUT';

export type PatternType =
  | 'FIXED_DAY_EXACT'
  | 'FIXED_DAY_PREVIOUS_BUSINESS_DAY'
  | 'FIXED_DAY_NEXT_BUSINESS_DAY'
  | 'END_OF_MONTH_BUSINESS_DAY'
  | 'END_OF_MONTH_FIXED';

export interface RegularExpenseForecastInsight {
  type: 'REGULAR_EXPENSE_FORECAST';
  key: string;
  displayName: string;
  expectedDate: string;
  patternType: PatternType;
}

export interface MonthOverMonthChangeInsight {
  type: 'MONTH_OVER_MONTH_CHANGE';
  key: string;
  displayName: string;
  changeAmount: string;
  changeRate: number;
  direction: TransactionDirection;
}

export interface UnusualLargeTransactionInsight {
  type: 'UNUSUAL_LARGE_TRANSACTION';
  key: string;
  displayName: string;
  direction: TransactionDirection;
}

export interface LargeProportionInsight {
  type: 'LARGE_PROPORTION';
  key: string;
  displayName: string;
  proportion: number;
  previousProportion: number;
  direction: TransactionDirection;
}

export type InsightItemResponse =
  | RegularExpenseForecastInsight
  | MonthOverMonthChangeInsight
  | UnusualLargeTransactionInsight
  | LargeProportionInsight;

export interface HomeInsightResponse {
  insights: InsightItemResponse[];
}

export const getHomeInsights = ({
  companyId,
  from,
  to,
}: {
  companyId: string;
  from: string;
  to: string;
}) => {
  return API.get<ResponseBaseWithData<HomeInsightResponse>>(
    `/api/v3/companies/${companyId}/home-dashboard/insights`,
    {
      params: { from, to },
    },
  );
};

export const markInsightsAsViewed = ({
  companyId,
  insightKeys,
}: {
  companyId: string;
  insightKeys: string[];
}) => {
  return API.post<ResponseBaseWithData<void>>(
    `/api/v3/companies/${companyId}/home-dashboard/insights/mark-as-viewed`,
    {
      data: { insightKeys },
    },
  );
};
